import type { SavvyTask } from 'src/types/task';
import { datadogRum } from '@datadog/browser-rum';
import { isNil, orderBy } from 'es-toolkit';
import { getTaskCreationHref as getTaskCreationRouteHref } from 'src/features/tasks/creation/task-creation-routes';

import type { TaskStatus, TaskSubStatus, TaskTypeName } from '@savvy/constants/generated/gql-enums';
import type { ID } from '@savvy/utils/id';

import { XLR8CS__Sub_Category_Task__c } from '../types/salesforce-task';
import { isCaptureOnlyCustodian } from './custodian';
import { DatadogRumAction } from './logging';
import { CLIENT_SERVICES_NAME, CLIENT_SERVICES_PROFILE_PIC_URL } from './salesforce/account';

export const CLIENT_SERVICES_SAVVY_USER_ID = '2080';

/**
 * Canonical identity for the Savvy Client Services (Ops) user, surfaced as a
 * selectable subtask assignee. The advisor-group query never returns this user,
 * so its name and avatar are hard-coded rather than resolved from a query.
 */
export const CLIENT_SERVICES_ASSIGNEE = {
  userId: CLIENT_SERVICES_SAVVY_USER_ID,
  fullName: CLIENT_SERVICES_NAME,
  firstName: 'Savvy',
  lastName: 'Client Services',
  profilePicUrl: CLIENT_SERVICES_PROFILE_PIC_URL,
} as const;

export function assigneeAvatarUrl(assignee: { id: string; profilePicUrl?: string | null }): string | null {
  if (assignee.id === CLIENT_SERVICES_SAVVY_USER_ID) {
    return CLIENT_SERVICES_PROFILE_PIC_URL;
  }
  return assignee.profilePicUrl ?? null;
}

// The Client Services (Ops) user's name never comes back from the assignee query, so it's
// resolved by id here; everyone else is first + last. Returns '' when neither name is present.
export function assigneeName(assignee: { id: string; firstName?: string | null; lastName?: string | null }): string {
  if (assignee.id === CLIENT_SERVICES_SAVVY_USER_ID) {
    return 'Savvy Client Services';
  }
  return [assignee.firstName, assignee.lastName].filter(Boolean).join(' ');
}

type TTask = {
  id: ID;
  type: TaskTypeName;
  user: { id: ID };
};

export function findFirstTaskByType<T extends TTask>(tasks: readonly T[], type: TaskTypeName, client_id?: ID) {
  const tasksLatestFirst = orderBy(tasks, [(task) => Number(task.id)], ['desc']);

  if (!isNil(client_id)) {
    return tasksLatestFirst.find((task) => task.type === type && task.user.id === client_id);
  }
  return tasksLatestFirst.find((task) => task.type === type);
}

export function findFirstTaskForUsersOfHousehold<T extends TTask>(tasks: readonly T[], type: TaskTypeName) {
  const householdMembers = tasks.map((task) => {
    return task.user;
  });
  const householdTasks: T[] = [];
  householdMembers.forEach((client) => {
    const clientTask = findFirstTaskByType(tasks, type, client.id);
    if (!isNil(clientTask)) {
      householdTasks.push(clientTask);
    }
  });
  return householdTasks;
}

export function isOnboardingTaskType(taskType: string[] | string): taskType is TaskTypeName {
  const onboardingTaskTypes: TaskTypeName[] = ['account_setup', 'initial_intake_form', 'onboarding_flow'];
  return onboardingTaskTypes.findIndex((onboardingTaskType) => taskType === onboardingTaskType) !== -1;
}

export function isTaskIncomplete(task: { status: TaskStatus }) {
  const incompleteStatuses: TaskStatus[] = ['todo', 'in_progress'];
  return incompleteStatuses.includes(task.status);
}

/**
 * Maps Salesforce task subcategories to Savvy task types.
 * Returns null if no corresponding task type exists.
 */
export function savvyTaskTypeFromSalesforceTaskType(
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c,
): SavvyTask | null {
  // This is not extensive of all the types that we have in Savvy. Just those SF task types that
  // we have manually defined on XLR8CS__Sub_Category_Task__c.
  // TODO: Sort out money movement and add all mappings.
  const mapping: Partial<Record<XLR8CS__Sub_Category_Task__c, SavvyTask>> = {
    [XLR8CS__Sub_Category_Task__c.SelfReminder]: 'self_reminder',
    [XLR8CS__Sub_Category_Task__c.OtherInvestmentTask]: 'other_investment_task',
    [XLR8CS__Sub_Category_Task__c.Other]: 'other',
    [XLR8CS__Sub_Category_Task__c.MoneyMovement]: 'money_movement_request',
    [XLR8CS__Sub_Category_Task__c.NewMoneyMovement]: 'money_movement_request',
    [XLR8CS__Sub_Category_Task__c.DEPRECEATED_MoneyMovement]: 'money_movement_request',
    [XLR8CS__Sub_Category_Task__c.InvestmentManagementDirectIndexing]: 'investment_management_direct_indexing',
    [XLR8CS__Sub_Category_Task__c.FinnancialPlanning]: 'financial_planning',
    [XLR8CS__Sub_Category_Task__c.ClientInfoChange]: 'client_info_change',
    [XLR8CS__Sub_Category_Task__c.AccountMaintenance]: 'account_maintenance',
    [XLR8CS__Sub_Category_Task__c.ClientRiskQuestionnaire]: 'risk_questionnaire',
    [XLR8CS__Sub_Category_Task__c.ClientPreReviewCheckIn]: 'pre_review_check_in',
    [XLR8CS__Sub_Category_Task__c.ClientIntake]: 'initial_intake_form',
    [XLR8CS__Sub_Category_Task__c.ClientOnboarding]: 'onboarding_flow',
    [XLR8CS__Sub_Category_Task__c.ClientAccountConfirmation]: 'account_setup',
    [XLR8CS__Sub_Category_Task__c.ClientFileUpload]: 'file_upload',
  };

  return mapping[salesforceSubCategory] ?? null;
}

export function taskTitleFromSubCategory(
  taskSubCategory?: XLR8CS__Sub_Category_Task__c,
  isAdvisorDirectedOnboardingEnabled = false,
) {
  if (!taskSubCategory) {
    return 'Internal task';
  }
  switch (taskSubCategory) {
    case XLR8CS__Sub_Category_Task__c.ClientIntake:
      return 'Intake';
    case XLR8CS__Sub_Category_Task__c.ClientOnboarding:
      return 'Onboarding';
    case XLR8CS__Sub_Category_Task__c.ClientFileUpload:
      return 'File Upload';
    case XLR8CS__Sub_Category_Task__c.ClientPreReviewCheckIn:
      return 'Pre-Review Check In';
    case XLR8CS__Sub_Category_Task__c.InvestmentManagementSWIM:
      return 'Savvy Wealth Investment Management';
    case XLR8CS__Sub_Category_Task__c.Trading:
      return 'Trading';
    case XLR8CS__Sub_Category_Task__c.ClientRiskQuestionnaire:
      return 'Risk Questionnaire';
    case XLR8CS__Sub_Category_Task__c.SelfReminder:
      // self reminder has visually been renamed to advisor task on the UI (remains self reminder in salesforce)
      return 'Advisor task';
    case XLR8CS__Sub_Category_Task__c.ClientAccountConfirmation: {
      return isAdvisorDirectedOnboardingEnabled ? 'Client Account Confirmation' : taskSubCategory;
    }
    case XLR8CS__Sub_Category_Task__c.ClientOther:
    case XLR8CS__Sub_Category_Task__c.Other:
    case XLR8CS__Sub_Category_Task__c.OtherInvestmentTask:
    case XLR8CS__Sub_Category_Task__c.MoneyMovement:
    case XLR8CS__Sub_Category_Task__c.NewMoneyMovement:
    case XLR8CS__Sub_Category_Task__c.DEPRECEATED_MoneyMovement:
    case XLR8CS__Sub_Category_Task__c.AccountMaintenance:
    case XLR8CS__Sub_Category_Task__c.ClientInfoChange:
    case XLR8CS__Sub_Category_Task__c.FinnancialPlanning:
    case XLR8CS__Sub_Category_Task__c.InvestmentManagementDirectIndexing:
    case XLR8CS__Sub_Category_Task__c.ClientReviewPresentation:
    case XLR8CS__Sub_Category_Task__c.InvestmentProposalPresentation:
    case XLR8CS__Sub_Category_Task__c.TransitionAnalysis:
  }
  return taskSubCategory;
}

export function taskTitleFromSavvyTaskType(savvyTaskType: SavvyTask | null) {
  if (!savvyTaskType) return null;

  // Not all tasks have a defaulted title- this is an inexhaustive list of the type to title mappings. Continue to add as needed.
  const mapping: Partial<Record<SavvyTask, string>> = {
    risk_questionnaire: 'Risk Questionnaire',
    pre_review_check_in: 'Pre-Review Check In',
    initial_intake_form: 'Intake',
    account_setup: 'Account Confirmation',
    onboarding_flow: 'Onboarding',
  };

  return mapping[savvyTaskType] ?? null;
}

type GetDefaultTaskAssigneeParams = {
  taskSubCategory: XLR8CS__Sub_Category_Task__c;
  csaId: string | null | undefined;
  advisorId: string | null | undefined;
};

export function getDefaultTaskAssignee({
  taskSubCategory,
  csaId,
  advisorId,
}: GetDefaultTaskAssigneeParams): string | null {
  switch (taskSubCategory) {
    case XLR8CS__Sub_Category_Task__c.ClientIntake:
    case XLR8CS__Sub_Category_Task__c.ClientFileUpload:
    case XLR8CS__Sub_Category_Task__c.ClientPreReviewCheckIn:
    case XLR8CS__Sub_Category_Task__c.ClientRiskQuestionnaire:
    case XLR8CS__Sub_Category_Task__c.SelfReminder:
      return advisorId ?? null;
    case XLR8CS__Sub_Category_Task__c.InvestmentManagementSWIM:
    case XLR8CS__Sub_Category_Task__c.Trading:
    case XLR8CS__Sub_Category_Task__c.OtherInvestmentTask:
    case XLR8CS__Sub_Category_Task__c.TransitionAnalysis:
    case XLR8CS__Sub_Category_Task__c.InvestmentProposalPresentation:
      return CLIENT_SERVICES_SAVVY_USER_ID;
    case XLR8CS__Sub_Category_Task__c.ClientOnboarding:
    case XLR8CS__Sub_Category_Task__c.ClientOther:
    case XLR8CS__Sub_Category_Task__c.ClientAccountConfirmation:
    case XLR8CS__Sub_Category_Task__c.Other:
    case XLR8CS__Sub_Category_Task__c.MoneyMovement:
    case XLR8CS__Sub_Category_Task__c.NewMoneyMovement:
    case XLR8CS__Sub_Category_Task__c.DEPRECEATED_MoneyMovement:
    case XLR8CS__Sub_Category_Task__c.AccountMaintenance:
    case XLR8CS__Sub_Category_Task__c.ClientInfoChange:
    case XLR8CS__Sub_Category_Task__c.FinnancialPlanning:
    case XLR8CS__Sub_Category_Task__c.InvestmentManagementDirectIndexing:
    case XLR8CS__Sub_Category_Task__c.ClientReviewPresentation:
    default:
      return csaId ?? null;
  }
}

export function logTaskUpdate(taskType: TaskTypeName, updateObj: object) {
  Object.keys(updateObj).forEach((key) => {
    datadogRum.addAction(DatadogRumAction.TaskEdited, {
      field: key,
      subcategory: taskType,
    });
  });
}

const disabledTaskTypes = new Set<TaskTypeName>([
  'onboarding_flow',
  'client_transition_flow',
  'swim_request',
  'transfer',
  'trades_cash_management',
  'trading_request',
]);

export function isTaskTypeDisabledForStatusUpdate(
  taskType: TaskTypeName,
  moneyMovementRequestItemVersion?: number | null,
) {
  return (
    disabledTaskTypes.has(taskType) || (taskType === 'money_movement_request' && moneyMovementRequestItemVersion === 2)
  );
}

// Capture-only custodians (e.g. Goldman Sachs) have no automated onboarding flow
// driving the task status, so ops/advisors set it manually — the onboarding_flow
// status lock (which exists to defer to that automation) shouldn't apply.
function isCaptureOnlyOnboardingTask(task: {
  type: TaskTypeName;
  onboardingTask?: { custodian?: string | null } | null;
}): boolean {
  return task.type === 'onboarding_flow' && isCaptureOnlyCustodian(task.onboardingTask?.custodian);
}

export function canReassignTask(
  task: { assignee?: { id: string } | null },
  { isAdmin }: { isAdmin: boolean },
): boolean {
  return isAdmin || task.assignee?.id !== CLIENT_SERVICES_SAVVY_USER_ID;
}

export function canEditTaskStatus(
  task: {
    assignee?: { id: string } | null;
    type: TaskTypeName;
    moneyMovementRequest?: { itemVersion?: number | null } | null;
    onboardingTask?: { custodian?: string | null } | null;
  },
  { isAdmin }: { isAdmin: boolean },
): boolean {
  const isClientServicesTask = task.assignee?.id === CLIENT_SERVICES_SAVVY_USER_ID;
  if (isClientServicesTask && !isAdmin) return false;

  // Keeps main's capture-only onboarding carve-out (UNO-572). The sub-status /
  // internal-task checks that used to live here are gone: a viewer who cannot set
  // sub-statuses can still move the task through the statuses the server accepts,
  // and the pickers narrow the options via isParkedForViewer.
  if (
    isTaskTypeDisabledForStatusUpdate(task.type, task.moneyMovementRequest?.itemVersion) &&
    !isAdmin &&
    !isCaptureOnlyOnboardingTask(task)
  ) {
    return false;
  }

  return true;
}

// Statuses a viewer without extended-palette rights may still pick on a task that
// already carries a sub-status. The server allows exactly these two, because ending
// the work has to null the sub-status to satisfy the DB CHECK constraint — see
// Mutations::Concerns::TaskWriteAuthorization::SUB_STATUS_CLEARING_TERMINAL_STATUSES.
// Anything else is a change to Client Services' state and is rejected.
export const SUB_STATUS_CLEARING_TERMINAL_STATUSES: readonly TaskStatus[] = ['done', 'cancelled'];

// True when the task is parked on a sub-status the viewer may not touch, so the
// picker must hide every status outside SUB_STATUS_CLEARING_TERMINAL_STATUSES rather
// than offer writes the server will reject.
export function isParkedForViewer(task: {
  subStatus: TaskSubStatus | null;
  canChangeExtendedStatus: boolean;
}): boolean {
  return task.subStatus !== null && !task.canChangeExtendedStatus;
}

// Builds the task-creation route for a given task-type slug. When a household
// salesforceAccountId is known we land on the client-scoped page (which pins the
// household); otherwise we open the global picker pre-selecting the type.
export function getTaskCreationHref(type: 'swim' | 'transition-analysis', salesforceAccountId?: string | null): string {
  return getTaskCreationRouteHref({ salesforceAccountId, slug: type });
}
